<template>
    <div class="pl-2">
        <div class="media-library-header pr-3">
            <v-text-field
                :label="$translate('Search...')"
                v-model="searchText"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-magnify"
                clearable
                class="search-field"
            />
            <v-btn
                @click="$refs.iuploader.click()"
                color="zoezi-edit-color"
                outlined
                height="40"
                class="upload-btn pr-4"
            >
                <input ref="iuploader" class="d-none" type="file" :accept="type == 'image' ? 'image/*' : (type == 'video' ? 'video/*' : 'image/*,video/*,application/pdf')" @change="uploadFile($event.target.files[0])">
                <v-icon left>mdi-upload</v-icon>
                {{ uploadTitle }}
            </v-btn>
        </div>
        <v-row class="media-library-filters pr-3" no-gutters align="center">
            <v-col>
                <v-select
                    v-model="filterTag"
                    :items="availableTags"
                    item-text="name"
                    item-value="id"
                    :label="$translate('Tag')"
                    dense
                    outlined
                    hide-details
                    clearable
                    style="margin-top: 0px !important;"
                />
            </v-col>
            <v-col cols="auto" class="pl-3">
                <v-btn-toggle v-model="filterType" dense borderless class="filter-type-toggle">
                    <v-btn :value="'image'" :title="$translate('Images')">
                        <v-icon>mdi-image</v-icon>
                    </v-btn>
                    <v-btn :value="'video'" :title="$translate('Videos')">
                        <v-icon>mdi-video</v-icon>
                    </v-btn>
                    <v-btn :value="'pdf'" :title="$translate('PDF')">
                        <v-icon>mdi-file-pdf-box</v-icon>
                    </v-btn>
                </v-btn-toggle>
            </v-col>
        </v-row>
        <v-item-group active-class="zoezi-edit-color" @change="fileChanged" v-model="selectedFile">
            <v-row v-for="(row, rowindex) in fileRows" :key="rowindex">
                <v-col :cols="12 / columns" v-for="file in row" :key="file.key">
                    <v-hover v-slot:default="{ hover }">
                        <v-item :value="file.key" v-slot:default="{ active, toggle}">
                            <v-card
                                width=200
                                @click="cardClicked($event, file, active, toggle)"
                            >
                                <!-- Video element with preload for videos, image for images -->
                                <div v-if="isVideo(file)" class="video-container">
                                    <video
                                        :ref="'video_' + file.key"
                                        class="video-element"
                                        preload="metadata"
                                        muted
                                        loop
                                        @mouseenter="playVideo(file.key)"
                                        @mouseleave="pauseVideo(file.key)"
                                    >
                                        <source :src="getFileUrl(file)" type="video/mp4">
                                    </video>
                                    <v-icon class="media-icon white--text" :title="file.content_type">
                                        mdi-video
                                    </v-icon>
                                    <v-btn v-if="hover" icon @click.stop="remove(file)" class="delete-btn"><v-icon style="color: white" large>mdi-close</v-icon></v-btn>
                                    <div class="text-truncate media-label white--text font-weight-bold text-h6" :title="file.name">
                                        {{ file.name }}
                                    </div>
                                </div>
                                <div v-else-if="isPdf(file)" class="pdf-container">
                                    <v-icon class="pdf-icon" x-large>mdi-file-pdf-box</v-icon>
                                    <v-btn v-if="hover" icon @click.stop="remove(file)" class="delete-btn"><v-icon style="color: white" large>mdi-close</v-icon></v-btn>
                                    <div class="text-truncate media-label white--text font-weight-bold text-h6" :title="file.name">
                                        {{ file.name }}
                                    </div>
                                </div>
                                <v-auto-img v-else :src="getFileUrl(file)" height="200px" width="200px">
                                    <v-icon class="media-icon white--text" :title="file.content_type">
                                        mdi-image
                                    </v-icon>
                                    <v-btn v-if="hover" icon @click.stop="remove(file)" class="delete-btn"><v-icon style="color: white" large>mdi-close</v-icon></v-btn>
                                    <div class="text-truncate media-label white--text font-weight-bold text-h6" :title="file.name">
                                        {{ file.name }}
                                    </div>
                                </v-auto-img>
                            </v-card>
                        </v-item>
                    </v-hover>
                </v-col>
            </v-row>
        </v-item-group>

        <v-btn v-if="limit < files.length"
               text
               color="primary"
               @click="limit += 100"
        >
            {{$translate('Load more')}}
        </v-btn>

        <v-dialog
            v-model="showProgress"
            max-width="500"
        >
            <v-card>
                <v-card-title class="headline zoezi-edit-color">{{ uploadTitle }}</v-card-title>

                <v-card-text class="pa-4">
                    <v-progress-linear
                        active
                        indeterminate
                        size="32"
                        :value="progress"
                        width="2"
                        color="zoezi-edit-color"
                    />
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn
                        text
                        color="error"
                        @click="showProgress=false"
                    >
                        {{$translate('Cancel')}}
                    </v-btn>
                </v-card-actions>

            </v-card>

        </v-dialog>

        <FileDialog
            v-model="fileDialog.show"
            :file="fileDialog.file"
            @updated="onFileUpdated"
        />
    </div>
</template>

<script>
import {chunk} from 'lodash';
import FileDialog from './FileDialog.vue';
export default {
    name: 'MediaLibrary',
    components: {FileDialog},
    props: {
        columns: {
            type: Number,
            default: 2
        },
        value: {
            type: String
        },
        type: {
            type: String
        },
        canShow: {
            type: Boolean
        }
    },
    data: () => ({
        files: [],
        selectedFile: null,
        fileToUpload: null,
        showProgress: false,
        progress: 0,
        limit: 100,
        searchText: '',
        filterType: null,
        filterTag: null,
        availableTags: [],

        fileDialog: {
            show: false,
            file: null
        }
    }),
    watch: {
        value(value) {
            console.log('Value changed for media lib')
            this.selectedFile = value;
        }
    },
    computed: {
        fileRows() {
            return chunk(this.sortedFiles, this.columns);
        },
        filteredFiles() {
            let result = this.files;
            if (this.searchText) {
                let s = this.searchText.toLowerCase();
                result = result.filter(file => file.name.toLowerCase().indexOf(s) != -1);
            }
            if (this.filterType) {
                result = result.filter(file => {
                    if (this.filterType === 'image') return file.content_type && file.content_type.startsWith('image/');
                    if (this.filterType === 'video') return file.content_type && file.content_type.startsWith('video/');
                    if (this.filterType === 'pdf') return file.content_type === 'application/pdf';
                    return true;
                });
            }
            if (this.filterTag) {
                result = result.filter(file => file.tags && file.tags.some(t => t.id === this.filterTag));
            }
            return result;
        },
        sortedFiles() {
            return this.filteredFiles.concat().sort((a, b) => -a.created.localeCompare(b.created)).slice(0, this.limit);
        },
        uploadTitle() {
            const t = this.type == 'image' ? 'image' : (this.type == 'video' ? 'video' : 'file')
            return this.$translate('Upload') + ' ' + this.$translate(t);
        }
    },
    methods: {
        getFileUrl(file) {
            return this.$api.getImageUrl(file, null, {notrack: true});
        },
        isVideo(file) {
            return file.content_type && file.content_type.startsWith('video/');
        },
        isPdf(file) {
            return file.content_type === 'application/pdf';
        },
        playVideo(key) {
            const video = this.$refs['video_' + key];
            if (video && video[0]) {
                video[0].play();
            }
        },
        pauseVideo(key) {
            const video = this.$refs['video_' + key];
            if (video && video[0]) {
                video[0].pause();
                video[0].currentTime = 0;
            }
        },
        fileChanged() {
            this.$emit('change', this.selectedFile)
        },
        isFileValid(file) {
            // Max size 16 MB
            if (file.size > 16 * 1024 * 1024) {
                this.$store.commit('showErrorDialog', {
                    title: this.$translate('Failed to upload file'),
                    text: this.$translate('The file is too big, max size is 16 MB')
                });
                return false;
            }

            if (!file.type.startsWith('image/') && !file.type.startsWith('video/') && file.type !== 'application/pdf') {
                this.$store.commit('showErrorDialog', {
                    title: this.$translate('Failed to upload file'),
                    text: this.$translate('Invalid file type. Only images, videos and PDFs are allowed.')
                });
                return false;
            }

            return true;
        },
        uploadFile(file) {
            if (!this.isFileValid(file)) {
                return;
            }

            this.showProgress = true;
            const formData = new FormData();
            formData.append('file', file);
            formData.append('data', JSON.stringify({origin_type: 'homepage'}));
            this.$api.post('/api/utils/file/upload', formData).then(response => response.json().then(data => {
                if (response.ok) {
                    this.files.push(data);
                    this.selectedFile = data.key;
                    this.$emit('change', data.key);
                    this.$store.commit('showSnackBar', {text: this.$translate('Upload successful'), color: 'success'});
                } else {
                    this.$store.commit('showErrorDialog', {
                        title: this.$translate('Failed to upload file'),
                        text: this.$translate(data.message)
                    });
                }
                // Clear files in iuploader
                this.$refs.iuploader.value = null;
                this.showProgress = false;
            }))
        },
        remove(file) {
            this.$store.commit('showConfirmDialog', {
                title: this.$translate('Delete file'),
                text: this.$translate('Are you sure you want to delete {0}?').format(file.name),
                actionText: this.$translate('Delete'),
                action: () => {
                    this.$api.post('/api/utils/file/remove', {key: file.key}).then(() => {
                        this.loadFiles();
                    })
                }
            });
        },
        loadFiles() {
            let type = this.type;
            let typeMatches = function(x) {
                if (!type) {
                    return true;
                }
                if (type == 'image') {
                    return x.is_image;
                }

                if (type == 'video') {
                    return x.content_type && x.content_type.startsWith('video/');
                }

                return true;
            }

            this.$api.post('/api/utils/file/get_list', {origin_type: 'homepage'}).then(response => response.json().then(data => {
                if (!response.ok || !Array.isArray(data)) {
                    this.files = [];
                    return;
                }
                this.files = data.filter(typeMatches).map(x => {
                    let f = {name: x.name, key: x.key, created: x.created, content_type: x.content_type, size: x.size, tags: x.tags || []};
                    if (x.is_image && !x.acl && x.storage == 4 && x.variants) f.variants = x.variants.split(',').map(Number);
                    return f;
                });
            }));
        },
        onFileUpdated() {
            this.$api.get('/api/systemtag/get').then(data => {
                this.availableTags = data;
            });
        },
        cardClicked($event, file, active, toggle) {
            if (this.canShow) {
                this.fileDialog.show = true;
                this.fileDialog.file = file;
            } else {
                if (active) {
                    this.fileChanged();
                } else {
                    toggle($event);
                }
            }
        }
    },
    mounted() {
        this.loadFiles();
        this.$api.get('/api/systemtag/get').then(data => {
            this.availableTags = data;
        });
    }
}
</script>

<style scoped>

.media-library-header {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 8px;
}

.search-field {
    max-width: 320px;
    flex-shrink: 0;
    flex-grow: 1;
}

.media-library-filters {
    margin-bottom: 8px;
}

.filter-type-toggle .v-btn {
    min-width: 40px !important;
    padding: 0 8px !important;
}

.filter-type-toggle .v-btn--active {
    color: var(--v-zoezi-edit-color-base, #7b1fa2) !important;
}

.upload-btn {
    white-space: nowrap;
    flex-shrink: 0;
    margin-left: auto;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    align-self: flex-end;
}

.video-container {
    position: relative;
    width: 100%;
    max-width: 200px;
    height: 200px;
    overflow: hidden;
}

.video-element {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.media-icon {
    position: absolute;
    top: 8px;
    left: 8px;
    text-shadow: 0 0 3px rgba(0,0,0,0.7);
}

.delete-btn {
    position: absolute;
    top: 0;
    right: 0;
    text-shadow: 0 0 3px rgba(0,0,0,0.8);
}

.delete-btn:hover {
    background-color: rgba(0, 0, 0, 0.6) !important;
}

.pdf-container {
    position: relative;
    width: 100%;
    max-width: 200px;
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #424242;
}

.pdf-icon {
    color: #ef5350;
    font-size: 64px !important;
}

.media-label {
    position: absolute;
    bottom: 8px;
    left: 8px;
    right: 8px;
    text-shadow: 0 0 3px rgba(0,0,0,0.8);
}
@media (max-width: 600px) {
    .media-library-header {
        flex-direction: column;
        align-items: stretch;
    }

    .search-field {
        max-width: 100%;
    }

    .upload-btn {
        width: 100%;
        margin-left: 0;
    }
}
</style>
